import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Youtube, ExternalLink } from 'lucide-react';
import { useT } from '../../i18n/I18nContext';

const FULL_LOGO_URL = "https://customer-assets.emergentagent.com/job_c868a3fd-bb7d-4698-a989-0d713ad2f0ec/artifacts/4rlq3789_WhatsApp%20Image%202026-01-16%20at%209.58.42%20AM.jpeg";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useT();

  const quickLinks = [
    { label: t('nav.about'), path: '/about', key: 'about' },
    { label: t('nav.smartechs'), path: '/smartechs', key: 'smartechs' },
    { label: t('nav.trainings'), path: '/trainings', key: 'trainings' },
    { label: t('nav.impact'), path: '/impact', key: 'impact' },
    { label: t('nav.news'), path: '/news', key: 'news' },
    { label: t('nav.contact'), path: '/contact', key: 'contact' },
  ];

  const trainings = [
    t('footer.trainings.wash'),
    t('footer.trainings.agri'),
    t('footer.trainings.waste'),
    t('footer.trainings.food'),
  ];

  return (
    <footer className="bg-primary text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo and Description */}
          <div className="lg:col-span-1">
            <img
              src={FULL_LOGO_URL}
              alt="SMART Centre Cameroon - Aspirant"
              className="h-20 w-auto object-contain mb-6 bg-white rounded-lg p-2"
            />
            <p className="text-blue-100 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.path}
                    className="text-blue-100 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${item.key}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Trainings */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.ourTrainings')}</h3>
            <ul className="space-y-3">
              {trainings.map((item) => (
                <li key={item}>
                  <Link
                    to="/trainings"
                    className="text-blue-100 hover:text-white transition-colors text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.contactUs')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <div className="text-blue-100 text-sm">
                  <p className="font-medium text-white">{t('footer.limbeHQ')}</p>
                  <p>{t('footer.limbeAddress')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <div className="text-blue-100 text-sm">
                  <p className="font-medium text-white">{t('footer.tikoSatellite')}</p>
                  <p>{t('footer.tikoAddress')}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-secondary flex-shrink-0" />
                <a href="tel:+237676837068" className="text-blue-100 hover:text-white transition-colors text-sm">
                  +237 676 837 068
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-secondary flex-shrink-0" />
                <a href="mailto:smartcentrecameroon@gmail.com" className="text-blue-100 hover:text-white transition-colors text-sm">
                  smartcentrecameroon@gmail.com
                </a>
              </li>
            </ul>

            {/* Social Links */}
            <div className="mt-6 flex items-center gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=100093268699335"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-secondary rounded-full flex items-center justify-center transition-colors"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://youtube.com/@sucdaplateform?si=1BRdc2usjgzcV03l"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                data-testid="social-youtube"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-blue-100 text-sm">
              © {currentYear} {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a
                href="https://smartcentregroup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-100 hover:text-white transition-colors flex items-center gap-1"
                data-testid="smart-centre-group-link"
              >
                SMART Centre Group <ExternalLink className="h-3 w-3" />
              </a>
              <span className="text-blue-100">|</span>
              <span className="text-blue-100">{t('footer.regLabel')}: TPPRR/RC/LBE/2022/B/0198</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
